import { LOJAS_DATA } from "@/app/utils/utils";
import { getPersistedUtmParams } from "@/app/utils/utm";
import { v4 as uuidv4 } from "uuid";

export const ALAN_WHATSAPP_NUMBER = "553197827643";

export interface AdReferralDto {
	source?: string;
	source_url?: string;
	campaign?: string;
	content?: string;
	term?: string;
	occurred_at?: string;
}

export interface AdReferralLeadDto {
	phonenumber?: string;
	name?: string;
	email?: string;
	external_id?: string;
	target_product?: string;
	dealership_name?: string;
	dealership_uf?: string;
	ad_referral?: AdReferralDto;
}

export function isSyonetEnabled(): boolean {
	return process.env.SYONET_ENABLED === "true";
}

export function isToyotaBrand(brand?: string): boolean {
	return (brand || "toyota").toLowerCase() === "toyota";
}

export function toAlanPhone(phone: string): string {
	const digits = (phone || "").replace(/\D/g, "");
	if (!digits) return "";
	if (digits.startsWith("55")) return digits;
	return `55${digits.replace(/^0+/, "")}`;
}

export function resolveDealershipUf(dealerName: string): string {
	const normalized = (dealerName || "").trim().toUpperCase();
	if (!normalized) return "MG";

	const loja = LOJAS_DATA.find((item) => {
		const nome = item.nome.toUpperCase();
		return (
			nome === normalized ||
			normalized.includes(nome) ||
			nome.includes(normalized)
		);
	});

	const address = loja?.endereço || "";
	const match =
		address.match(/[\/\-]\s*([A-Z]{2})\b/) ||
		address.match(/\b([A-Z]{2})\s*-?\s*CEP/i);
	return match?.[1]?.toUpperCase() || "MG";
}

export function buildAlanWhatsAppMessage(
	name: string,
	dealer: string,
	model: string
): string {
	const safeName = (name || "").trim() || "não informado";
	const safeDealer = (dealer || "").trim() || "não informada";
	const safeModel = (model || "").trim() || "não informado";
	return `Olá! Vim do site e meu nome é ${safeName}. Tenho interesse no modelo ${safeModel} na loja ${safeDealer}.`;
}

export function buildAlanWhatsAppUrl(
	name: string,
	dealer: string,
	model: string
): string {
	return `https://wa.me/${ALAN_WHATSAPP_NUMBER}?text=${encodeURIComponent(
		buildAlanWhatsAppMessage(name, dealer, model)
	)}`;
}

export function redirectToAlanWhatsApp(
	name: string,
	dealer: string,
	model: string
): void {
	if (typeof window === "undefined") return;
	window.location.href = buildAlanWhatsAppUrl(name, dealer, model);
}

function resolveUtmParams(utmParams?: string): URLSearchParams {
	if (utmParams) return new URLSearchParams(utmParams);

	if (typeof window !== "undefined") {
		const persisted = getPersistedUtmParams();
		if (persisted) return new URLSearchParams(persisted);
		return new URLSearchParams(window.location.search);
	}

	return new URLSearchParams();
}

export function buildAdReferralLeadPayload(input: {
	name: string;
	email?: string;
	phone: string;
	model?: string;
	dealer?: string;
	page?: string;
	externalId?: string;
	sourceUrl?: string;
	utmParams?: string;
}): AdReferralLeadDto {
	const utm = resolveUtmParams(input.utmParams);
	const sourceUrl =
		input.sourceUrl ||
		(typeof window !== "undefined"
			? window.location.href
			: input.page || "");
	const documentDigits = (input.externalId || "").replace(/\D/g, "");

	return {
		phonenumber: toAlanPhone(input.phone),
		name: (input.name || "").trim(),
		email: (input.email || "").trim() || undefined,
		external_id: documentDigits || uuidv4(),
		target_product: (input.model || "").trim() || undefined,
		dealership_name: (input.dealer || "").trim() || undefined,
		dealership_uf: resolveDealershipUf(input.dealer || ""),
		ad_referral: {
			source: utm.get("utm_source") || "site",
			source_url: sourceUrl,
			campaign: utm.get("utm_campaign") || undefined,
			content: utm.get("utm_content") || undefined,
			term: utm.get("utm_term") || undefined,
			occurred_at: new Date().toISOString(),
		},
	};
}

export async function sendToAlan(
	payload: AdReferralLeadDto
): Promise<boolean> {
	try {
		const response = await fetch("/api/alan", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(payload),
		});

		if (!response.ok) {
			const details = await response.text();
			console.error("[Alan] Erro ao enviar lead:", response.status, details);
			return false;
		}

		return true;
	} catch (error) {
		console.error("[Alan] Erro na integração:", error);
		return false;
	}
}
